/**
 * RapidAnswer.tsx — W54 Rapid Answer Mode
 *
 * Route: /account/:id/answer
 *
 * Focused keyboard-driven surface for walking CP1-CP10 and answering
 * questions one at a time. Deep-links via ?cp=N&q=<questionKey>.
 *
 * Layout:
 *   Top bar   — account name, M-PACT score, current CP, progress bar
 *   Center    — one question, large, with keyboard-selectable answer options
 *   Below     — question metadata, coaching note, Notes field
 *   Bottom    — nav buttons: Previous, Next, Skip, Jump to CP
 *   Right     — mini CP progress strip (CP1-CP10 with status indicators)
 *
 * Keyboard shortcuts:
 *   1-5       — select answer option (1=option 1, etc.)
 *   Enter     — save and advance to next question
 *   Backspace — go to previous question
 *   S         — skip (advance without saving)
 *   J         — open CP jump picker
 *   Esc       — close jump picker / clear selection
 *
 * Behavior:
 *   - Walks CP1-CP10 in order, questions in CP order
 *   - Each answer autosaves via existing useAnswerLane hook
 *   - URL params ?cp=N&q=<key> deep-link to specific question
 *   - On completion, shows summary with link to /account/:id/command-view
 *
 * Canon: no exclamation points, no emojis, M-PACT hyphenated, Checkpoint not Gate.
 * Palette: Gray #6B7280, Red #DC2626, Orange #EA580C, Yellow #EAB308, Green #16A34A.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useSearch, useLocation } from "wouter";
import OperatorShell from "@/components/operator/OperatorShell";
import {
  useAnswerLane,
  CP_SHORT_NAMES,
  CP_NAMES,
  type Question,
  type SavePayload,
} from "@/hooks/useAnswerLane";

// ─── Canon palette ────────────────────────────────────────────────────────────
const PAL = {
  gray:   "#6B7280",
  red:    "#DC2626",
  orange: "#EA580C",
  yellow: "#EAB308",
  green:  "#16A34A",
  black:  "#0B0B0F",
  white:  "#FFFFFF",
  bg:     "#FAFAF7",
  border: "#E5E5E7",
  muted:  "#6B6B70",
  soft:   "#EFEFEE",
} as const;

// ─── Graded answer options (canonical 5-point scale) ─────────────────────────
const GRADED_OPTIONS = [
  { label: "Strong yes — confirmed / fully in place", value: "Strong",  points: 4, color: PAL.green },
  { label: "Mostly yes — likely / mostly in place",   value: "Mostly",  points: 3, color: PAL.green },
  { label: "Partial — mixed signals / unclear",       value: "Partial", points: 2, color: PAL.yellow },
  { label: "Weak — mostly missing / mostly no",       value: "Weak",    points: 1, color: PAL.orange },
  { label: "Absent — hard no / blocked",              value: "Absent",  points: 0, color: PAL.red },
];

// ─── CP progress strip item ───────────────────────────────────────────────────
function CpStripItem({
  cpNum,
  answered,
  total,
  isCurrent,
  onClick,
}: {
  cpNum: number;
  answered: number;
  total: number;
  isCurrent: boolean;
  onClick: () => void;
}) {
  const pct = total > 0 ? Math.round((answered / total) * 100) : 0;
  const color =
    pct === 100 ? PAL.green
    : pct > 0   ? PAL.yellow
    : PAL.gray;

  return (
    <button
      onClick={onClick}
      title={`${CP_SHORT_NAMES[cpNum]} — ${answered}/${total} answered`}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 4,
        padding: "6px 4px",
        borderRadius: 6,
        border: isCurrent ? `2px solid ${PAL.black}` : `2px solid transparent`,
        background: isCurrent ? "#F4F4F5" : "transparent",
        cursor: "pointer",
        minWidth: 36,
        transition: "background 0.1s",
      }}
    >
      <div
        style={{
          width: 22,
          height: 22,
          borderRadius: "50%",
          background: isCurrent ? PAL.black : color,
          color: PAL.white,
          fontSize: 10,
          fontWeight: 700,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {cpNum}
      </div>
      <div style={{ fontSize: 9, color: isCurrent ? PAL.black : PAL.muted, fontWeight: isCurrent ? 700 : 400 }}>
        {answered}/{total}
      </div>
      {/* Mini progress bar */}
      <div style={{ width: "100%", height: 3, background: PAL.soft, borderRadius: 9999 }}>
        <div
          style={{
            height: "100%",
            width: `${pct}%`,
            background: isCurrent ? PAL.black : color,
            borderRadius: 9999,
            transition: "width 0.3s",
          }}
        />
      </div>
    </button>
  );
}

// ─── Completion screen ────────────────────────────────────────────────────────
function CompletionScreen({
  accountId,
  accountName,
  totalAnswered,
  totalQuestions,
}: {
  accountId: number;
  accountName: string;
  totalAnswered: number;
  totalQuestions: number;
}) {
  const [, navigate] = useLocation();
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: 400,
        padding: 32,
        textAlign: "center",
      }}
    >
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: "50%",
          background: PAL.green,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: 20,
        }}
      >
        <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
          <path d="M6 14l6 6L22 8" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      <h2 style={{ fontSize: 20, fontWeight: 800, color: PAL.black, marginBottom: 8 }}>
        All checkpoints complete
      </h2>
      <p style={{ fontSize: 14, color: PAL.muted, marginBottom: 4 }}>
        {accountName}
      </p>
      <p style={{ fontSize: 13, color: PAL.muted, marginBottom: 28 }}>
        {totalAnswered} of {totalQuestions} questions answered
      </p>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", justifyContent: "center" }}>
        <button
          onClick={() => navigate(`/account/${accountId}/command-view`)}
          style={{
            padding: "10px 20px",
            background: PAL.black,
            color: PAL.white,
            border: "none",
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 700,
            cursor: "pointer",
            letterSpacing: "0.03em",
          }}
        >
          View Command View
        </button>
        <button
          onClick={() => navigate(`/account/${accountId}`)}
          style={{
            padding: "10px 20px",
            background: PAL.white,
            color: PAL.black,
            border: `1px solid ${PAL.border}`,
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 700,
            cursor: "pointer",
            letterSpacing: "0.03em",
          }}
        >
          Back to Account
        </button>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function RapidAnswer() {
  const params = useParams<{ id: string }>();
  const search = useSearch();
  const [, navigate] = useLocation();
  const accountId = parseInt(params.id ?? "0", 10);

  // Parse deep-link query params
  const urlParams = new URLSearchParams(search);
  const initCp  = parseInt(urlParams.get("cp") ?? "0", 10) || null;
  const initKey = urlParams.get("q") ?? null;

  const {
    account,
    accountLoading,
    cpProgress,
    questions,
    questionsLoading,
    saveAnswer,
    savingKey,
    lastSavedAt,
    totalAnswered,
    totalQuestions,
  } = useAnswerLane(accountId);

  // ── Local state ───────────────────────────────────────────────────────────
  const [activeCp,       setActiveCp]       = useState<number>(initCp ?? 1);
  const [activeQuestion, setActiveQuestion] = useState<Question | null>(null);
  const [selectedValue,  setSelectedValue]  = useState<string | null>(null);
  const [notes,          setNotes]          = useState<string>("");
  const [isDirty,        setIsDirty]        = useState<boolean>(false);
  const [jumpOpen,       setJumpOpen]       = useState<boolean>(false);
  const [showCompletion, setShowCompletion] = useState<boolean>(false);

  const notesRef = useRef<HTMLTextAreaElement>(null);

  // ── CP questions ──────────────────────────────────────────────────────────
  const cpQuestions = questions.filter((q) => q.cp_number === activeCp);
  const activeIndex = activeQuestion
    ? cpQuestions.findIndex((q) => q.question_key === activeQuestion.question_key)
    : -1;

  // ── Initialize from URL params or first unanswered ────────────────────────
  useEffect(() => {
    if (questions.length === 0) return;
    if (activeQuestion) return;

    // Deep-link: jump to specific question
    if (initKey) {
      const target = questions.find((q) => q.question_key === initKey);
      if (target) {
        setActiveCp(target.cp_number);
        setActiveQuestion(target);
        return;
      }
    }

    // CP param: jump to first question in that CP
    if (initCp) {
      const target = questions.find((q) => q.cp_number === initCp && !q.is_answered)
        ?? questions.find((q) => q.cp_number === initCp);
      if (target) {
        setActiveCp(initCp);
        setActiveQuestion(target);
        return;
      }
    }

    // Default: first unanswered question globally
    const firstUnanswered = questions.find((q) => !q.is_answered);
    if (firstUnanswered) {
      setActiveCp(firstUnanswered.cp_number);
      setActiveQuestion(firstUnanswered);
      return;
    }

    // All answered — show completion
    if (questions.length > 0) {
      setShowCompletion(true);
    }
  }, [questions]);

  // ── Populate form from current answer when question changes ───────────────
  useEffect(() => {
    if (!activeQuestion) return;
    const cur = activeQuestion.current_answer;
    setSelectedValue(cur?.answer_value ?? null);
    setNotes(cur?.answer_notes ?? "");
    setIsDirty(false);
  }, [activeQuestion?.question_key]);

  // ── Navigation helpers ────────────────────────────────────────────────────
  function selectQuestion(q: Question) {
    setActiveQuestion(q);
    setActiveCp(q.cp_number);
    setIsDirty(false);
  }

  function goToCp(cp: number) {
    const first = questions.find((q) => q.cp_number === cp && !q.is_answered)
      ?? questions.find((q) => q.cp_number === cp);
    if (first) {
      setActiveCp(cp);
      setActiveQuestion(first);
      setIsDirty(false);
    }
    setJumpOpen(false);
  }

  function advance() {
    if (!activeQuestion) return;

    // Next unanswered in same CP
    const cpQs = questions.filter((q) => q.cp_number === activeCp);
    const curIdx = cpQs.findIndex((q) => q.question_key === activeQuestion.question_key);
    const nextInCp = cpQs.slice(curIdx + 1).find((q) => !q.is_answered);
    if (nextInCp) { selectQuestion(nextInCp); return; }

    // Move to first unanswered in next CP
    for (let cp = activeCp + 1; cp <= 10; cp++) {
      const q = questions.find((q) => q.cp_number === cp && !q.is_answered);
      if (q) { selectQuestion(q); return; }
    }

    // All done
    setShowCompletion(true);
  }

  function goBack() {
    if (!activeQuestion) return;
    const cpQs = questions.filter((q) => q.cp_number === activeCp);
    const curIdx = cpQs.findIndex((q) => q.question_key === activeQuestion.question_key);
    if (curIdx > 0) {
      selectQuestion(cpQs[curIdx - 1]);
      return;
    }
    // Go to last question in previous CP
    for (let cp = activeCp - 1; cp >= 1; cp--) {
      const cpQsP = questions.filter((q) => q.cp_number === cp);
      if (cpQsP.length > 0) {
        selectQuestion(cpQsP[cpQsP.length - 1]);
        return;
      }
    }
  }

  async function handleSaveAndNext() {
    if (!activeQuestion || !selectedValue) {
      advance();
      return;
    }
    const payload: SavePayload = {
      answer_value: selectedValue,
      answer_notes: notes.trim() || undefined,
    };
    await saveAnswer(activeQuestion.question_key, payload, { immediate: true });
    setIsDirty(false);
    advance();
  }

  function handleSkip() {
    advance();
  }

  // ── Keyboard handler ──────────────────────────────────────────────────────
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      // Ignore when typing in textarea
      if (e.target instanceof HTMLTextAreaElement) {
        if (e.key === "Escape") { (e.target as HTMLTextAreaElement).blur(); }
        return;
      }
      if (e.target instanceof HTMLInputElement) return;

      if (jumpOpen) {
        if (e.key === "Escape") { setJumpOpen(false); return; }
        return;
      }

      switch (e.key) {
        case "1": case "2": case "3": case "4": case "5": {
          const idx = parseInt(e.key, 10) - 1;
          if (GRADED_OPTIONS[idx]) {
            setSelectedValue(GRADED_OPTIONS[idx].value);
            setIsDirty(true);
          }
          break;
        }
        case "Enter": {
          e.preventDefault();
          void handleSaveAndNext();
          break;
        }
        case "Backspace": {
          if (!isDirty) goBack();
          break;
        }
        case "s": case "S": {
          handleSkip();
          break;
        }
        case "j": case "J": {
          setJumpOpen((v) => !v);
          break;
        }
        case "Escape": {
          setSelectedValue(activeQuestion?.current_answer?.answer_value ?? null);
          setNotes(activeQuestion?.current_answer?.answer_notes ?? "");
          setIsDirty(false);
          break;
        }
      }
    }

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [activeQuestion, selectedValue, isDirty, jumpOpen]);

  // ── Derived data ──────────────────────────────────────────────────────────
  const score = account?.composite_score ?? account?.score ?? null;

  // CP progress lookup by cp_number
  const cpProgressMap = Object.fromEntries(
    cpProgress.map((p) => [p.cp_number, p])
  );

  // Current CP progress
  const curCpProgress = cpProgressMap[activeCp];
  const curCpAnswered = curCpProgress?.answered ?? 0;
  const curCpTotal    = curCpProgress?.total ?? cpQuestions.length;

  // Global progress bar
  const globalPct = totalQuestions > 0 ? Math.round((totalAnswered / totalQuestions) * 100) : 0;

  // ── Loading state ─────────────────────────────────────────────────────────
  if (accountLoading || questionsLoading) {
    return (
      <OperatorShell
        breadcrumbs={[
          { label: "Accounts", href: "/accounts" },
          { label: "Loading..." },
        ]}
        pageTitle="Rapid Answer"
      >
        <div style={{ padding: 40, color: PAL.muted, fontSize: 14 }}>
          Loading...
        </div>
      </OperatorShell>
    );
  }

  // ── Completion screen ─────────────────────────────────────────────────────
  if (showCompletion) {
    return (
      <OperatorShell
        breadcrumbs={[
          { label: "Accounts", href: "/accounts" },
          { label: account?.name ?? `Account ${accountId}`, href: `/account/${accountId}` },
          { label: "Rapid Answer" },
        ]}
        pageTitle="Rapid Answer"
      >
        <CompletionScreen
          accountId={accountId}
          accountName={account?.name ?? `Account ${accountId}`}
          totalAnswered={totalAnswered}
          totalQuestions={totalQuestions}
        />
      </OperatorShell>
    );
  }

  // ── Main layout ───────────────────────────────────────────────────────────
  return (
    <OperatorShell
      breadcrumbs={[
        { label: "Accounts", href: "/accounts" },
        { label: account?.name ?? `Account ${accountId}`, href: `/account/${accountId}` },
        { label: "Rapid Answer" },
      ]}
      pageTitle="Rapid Answer"
      pageMeta={account?.name ?? undefined}
    >
      {/* ── Top bar: account header + global progress ─── */}
      <div
        style={{
          background: PAL.white,
          border: `1px solid ${PAL.border}`,
          borderRadius: 10,
          padding: "12px 16px",
          marginBottom: 16,
          display: "flex",
          alignItems: "center",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        {/* Account name */}
        <div style={{ flex: 1, minWidth: 180 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: PAL.muted, marginBottom: 2 }}>
            Account
          </div>
          <div style={{ fontSize: 15, fontWeight: 700, color: PAL.black }}>
            {account?.name ?? `Account ${accountId}`}
          </div>
        </div>

        {/* Composite score */}
        {score !== null && score !== undefined && (
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: PAL.muted, marginBottom: 2 }}>
              Composite score
            </div>
            <div
              style={{
                fontSize: 22,
                fontWeight: 800,
                color:
                  score >= 75 ? PAL.green
                  : score >= 50 ? PAL.yellow
                  : score >= 25 ? PAL.orange
                  : PAL.red,
              }}
            >
              {Math.round(score)}
            </div>
          </div>
        )}

        {/* Current CP */}
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: PAL.muted, marginBottom: 2 }}>
            Current CP
          </div>
          <div style={{ fontSize: 15, fontWeight: 700, color: PAL.black }}>
            CP{activeCp}
          </div>
        </div>

        {/* Global progress */}
        <div style={{ flex: 1, minWidth: 160 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: PAL.muted, textTransform: "uppercase", letterSpacing: "0.08em" }}>Progress</span>
            <span style={{ fontSize: 11, fontWeight: 700, color: PAL.black }}>{totalAnswered} of {totalQuestions}</span>
          </div>
          <div style={{ height: 6, background: PAL.soft, borderRadius: 9999, overflow: "hidden" }}>
            <div
              style={{
                height: "100%",
                width: `${globalPct}%`,
                background: globalPct === 100 ? PAL.green : PAL.black,
                borderRadius: 9999,
                transition: "width 0.3s",
              }}
            />
          </div>
          <div style={{ fontSize: 10, color: PAL.muted, marginTop: 3 }}>{globalPct}% weighted score addressed</div>
        </div>

        {/* Save indicator */}
        <div style={{ fontSize: 11, color: PAL.muted, minWidth: 80, textAlign: "right" }}>
          {savingKey ? "Saving..." : lastSavedAt ? `Saved ${lastSavedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : ""}
        </div>
      </div>

      {/* ── Two-column body: question + right rail ─── */}
      <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>

        {/* ── CENTER: question area ── */}
        <div style={{ flex: 1, minWidth: 0 }}>

          {/* CP header */}
          <div
            style={{
              background: PAL.black,
              borderRadius: "10px 10px 0 0",
              padding: "10px 16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.6)", marginBottom: 2 }}>
                {CP_NAMES[activeCp] ?? `Checkpoint ${activeCp}`}
              </div>
              <div style={{ fontSize: 12, color: "rgba(255,255,255,0.8)" }}>
                Question {activeIndex + 1} of {cpQuestions.length} in this checkpoint
                {curCpTotal > 0 && (
                  <span style={{ marginLeft: 8, color: "rgba(255,255,255,0.5)" }}>
                    ({curCpAnswered}/{curCpTotal} answered)
                  </span>
                )}
              </div>
            </div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", textAlign: "right" }}>
              <span title="Press J to jump to a CP">J: Jump</span>
              {" · "}
              <span title="Press S to skip">S: Skip</span>
            </div>
          </div>

          {/* Question body */}
          <div
            style={{
              background: PAL.white,
              border: `1px solid ${PAL.border}`,
              borderTop: "none",
              borderRadius: "0 0 10px 10px",
              padding: "20px 20px 16px",
            }}
          >
            {!activeQuestion ? (
              <div style={{ color: PAL.muted, fontSize: 14, padding: "20px 0" }}>
                No question selected. Use the CP strip on the right to navigate.
              </div>
            ) : (
              <>
                {/* Question text */}
                <p
                  style={{
                    fontSize: 17,
                    fontWeight: 700,
                    color: PAL.black,
                    lineHeight: 1.5,
                    marginBottom: 20,
                  }}
                >
                  {activeQuestion.question_text}
                </p>

                {/* Answer options */}
                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
                  {GRADED_OPTIONS.map((opt, idx) => {
                    const isSelected = selectedValue === opt.value;
                    return (
                      <button
                        key={opt.value}
                        onClick={() => {
                          setSelectedValue(isSelected ? null : opt.value);
                          setIsDirty(true);
                        }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                          padding: "10px 14px",
                          borderRadius: 8,
                          border: isSelected ? `2px solid ${opt.color}` : `1px solid ${PAL.border}`,
                          background: isSelected ? `${opt.color}14` : PAL.white,
                          cursor: "pointer",
                          textAlign: "left",
                          transition: "all 0.1s",
                        }}
                      >
                        {/* Keyboard hint bubble */}
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 22,
                            height: 22,
                            borderRadius: 5,
                            background: isSelected ? opt.color : PAL.soft,
                            color: isSelected ? PAL.white : PAL.muted,
                            fontSize: 11,
                            fontWeight: 700,
                            flexShrink: 0,
                          }}
                        >
                          {idx + 1}
                        </span>
                        <span style={{ fontSize: 13, fontWeight: isSelected ? 700 : 400, color: isSelected ? opt.color : PAL.black, flex: 1 }}>
                          {opt.label}
                        </span>
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: isSelected ? opt.color : PAL.muted,
                            background: isSelected ? `${opt.color}18` : PAL.soft,
                            borderRadius: 9999,
                            padding: "2px 7px",
                            flexShrink: 0,
                          }}
                        >
                          {opt.points}pt
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* Question metadata */}
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    flexWrap: "wrap",
                    marginBottom: 14,
                  }}
                >
                  <span style={{ fontSize: 10, fontWeight: 600, color: PAL.muted, background: PAL.bg, border: `1px solid ${PAL.border}`, borderRadius: 9999, padding: "2px 9px" }}>
                    CP{activeQuestion.cp_number}
                  </span>
                  {activeQuestion.weight && (
                    <span style={{ fontSize: 10, fontWeight: 600, color: PAL.muted, background: PAL.bg, border: `1px solid ${PAL.border}`, borderRadius: 9999, padding: "2px 9px" }}>
                      Weight: {activeQuestion.weight}
                    </span>
                  )}
                  {activeQuestion.response_type && (
                    <span style={{ fontSize: 10, fontWeight: 600, color: PAL.muted, background: PAL.bg, border: `1px solid ${PAL.border}`, borderRadius: 9999, padding: "2px 9px" }}>
                      {activeQuestion.response_type}
                    </span>
                  )}
                  {activeQuestion.is_answered && (
                    <span style={{ fontSize: 10, fontWeight: 700, color: PAL.green, background: "#F0FDF4", border: `1px solid #BBF7D0`, borderRadius: 9999, padding: "2px 9px" }}>
                      Previously answered
                    </span>
                  )}
                </div>

                {/* Notes field */}
                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: "block", fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: PAL.muted, marginBottom: 6 }}>
                    Notes
                  </label>
                  <textarea
                    ref={notesRef}
                    value={notes}
                    onChange={(e) => { setNotes(e.target.value); setIsDirty(true); }}
                    placeholder="Optional context, evidence reference, or follow-up action..."
                    rows={3}
                    maxLength={500}
                    style={{
                      width: "100%",
                      boxSizing: "border-box",
                      padding: "9px 12px",
                      fontSize: 13,
                      color: PAL.black,
                      background: PAL.bg,
                      border: `1px solid ${PAL.border}`,
                      borderRadius: 7,
                      resize: "vertical",
                      outline: "none",
                      lineHeight: 1.5,
                    }}
                  />
                </div>

                {/* Bottom nav buttons */}
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <button
                    onClick={goBack}
                    title="Backspace"
                    style={{
                      padding: "8px 14px",
                      background: PAL.white,
                      border: `1px solid ${PAL.border}`,
                      borderRadius: 7,
                      fontSize: 12,
                      fontWeight: 700,
                      color: PAL.black,
                      cursor: "pointer",
                      letterSpacing: "0.04em",
                    }}
                  >
                    Previous
                  </button>

                  <button
                    onClick={() => void handleSaveAndNext()}
                    disabled={!selectedValue}
                    style={{
                      padding: "8px 20px",
                      background: selectedValue ? PAL.black : "rgba(11,11,15,0.2)",
                      border: "none",
                      borderRadius: 7,
                      fontSize: 12,
                      fontWeight: 700,
                      color: PAL.white,
                      cursor: selectedValue ? "pointer" : "not-allowed",
                      letterSpacing: "0.04em",
                    }}
                    title="Enter"
                  >
                    {savingKey === activeQuestion.question_key ? "Saving..." : "Save and Next"}
                  </button>

                  <button
                    onClick={handleSkip}
                    title="S"
                    style={{
                      padding: "8px 14px",
                      background: PAL.white,
                      border: `1px solid ${PAL.border}`,
                      borderRadius: 7,
                      fontSize: 12,
                      fontWeight: 700,
                      color: PAL.muted,
                      cursor: "pointer",
                      letterSpacing: "0.04em",
                    }}
                  >
                    Skip
                  </button>

                  <button
                    onClick={() => setJumpOpen((v) => !v)}
                    title="J"
                    style={{
                      padding: "8px 14px",
                      background: PAL.white,
                      border: `1px solid ${PAL.border}`,
                      borderRadius: 7,
                      fontSize: 12,
                      fontWeight: 700,
                      color: PAL.muted,
                      cursor: "pointer",
                      letterSpacing: "0.04em",
                    }}
                  >
                    Jump to CP
                  </button>

                  <div style={{ marginLeft: "auto", fontSize: 11, color: PAL.muted }}>
                    <kbd style={{ background: PAL.soft, border: `1px solid ${PAL.border}`, borderRadius: 3, padding: "1px 4px", fontSize: 10 }}>1-5</kbd> select
                    {" · "}
                    <kbd style={{ background: PAL.soft, border: `1px solid ${PAL.border}`, borderRadius: 3, padding: "1px 4px", fontSize: 10 }}>Enter</kbd> save
                    {" · "}
                    <kbd style={{ background: PAL.soft, border: `1px solid ${PAL.border}`, borderRadius: 3, padding: "1px 4px", fontSize: 10 }}>S</kbd> skip
                  </div>
                </div>
              </>
            )}
          </div>

          {/* CP questions list (mini navigator below main question) */}
          {cpQuestions.length > 1 && (
            <div
              style={{
                marginTop: 12,
                background: PAL.white,
                border: `1px solid ${PAL.border}`,
                borderRadius: 10,
                padding: "10px 14px",
              }}
            >
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: PAL.muted, marginBottom: 8 }}>
                Questions in CP{activeCp}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {cpQuestions.map((q, idx) => {
                  const isCur = q.question_key === activeQuestion?.question_key;
                  return (
                    <button
                      key={q.question_key}
                      onClick={() => selectQuestion(q)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "6px 10px",
                        borderRadius: 6,
                        border: isCur ? `1px solid ${PAL.black}` : `1px solid transparent`,
                        background: isCur ? "#F4F4F5" : "transparent",
                        cursor: "pointer",
                        textAlign: "left",
                      }}
                    >
                      <span
                        style={{
                          width: 18,
                          height: 18,
                          borderRadius: "50%",
                          background: q.is_answered ? PAL.green : isCur ? PAL.black : PAL.soft,
                          color: q.is_answered || isCur ? PAL.white : PAL.muted,
                          fontSize: 9,
                          fontWeight: 700,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        {idx + 1}
                      </span>
                      <span style={{ fontSize: 12, color: isCur ? PAL.black : PAL.muted, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {q.question_text}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* ── RIGHT RAIL: CP progress strip ── */}
        <div
          style={{
            width: 60,
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            gap: 4,
            position: "sticky",
            top: 80,
          }}
        >
          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: PAL.muted, marginBottom: 4, textAlign: "center" }}>
            CPs
          </div>
          {Array.from({ length: 10 }, (_, i) => i + 1).map((cp) => {
            const prog = cpProgressMap[cp];
            const answered = prog?.answered ?? 0;
            const total = prog?.total ?? questions.filter((q) => q.cp_number === cp).length;
            return (
              <CpStripItem
                key={cp}
                cpNum={cp}
                answered={answered}
                total={total}
                isCurrent={cp === activeCp}
                onClick={() => goToCp(cp)}
              />
            );
          })}
        </div>
      </div>

      {/* ── CP Jump Picker modal ── */}
      {jumpOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1000,
            background: "rgba(11,11,15,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
          onClick={(e) => e.target === e.currentTarget && setJumpOpen(false)}
        >
          <div
            style={{
              background: PAL.white,
              borderRadius: 12,
              padding: "20px 20px 16px",
              width: "100%",
              maxWidth: 380,
              boxShadow: "0 8px 32px rgba(11,11,15,0.18)",
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: PAL.muted, marginBottom: 14 }}>
              Jump to Checkpoint
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {Array.from({ length: 10 }, (_, i) => i + 1).map((cp) => {
                const prog = cpProgressMap[cp];
                const answered = prog?.answered ?? 0;
                const total = prog?.total ?? questions.filter((q) => q.cp_number === cp).length;
                const pct = total > 0 ? Math.round((answered / total) * 100) : 0;
                return (
                  <button
                    key={cp}
                    onClick={() => goToCp(cp)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "8px 12px",
                      borderRadius: 7,
                      border: cp === activeCp ? `1px solid ${PAL.black}` : `1px solid transparent`,
                      background: cp === activeCp ? "#F4F4F5" : "transparent",
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                  >
                    <div
                      style={{
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        background: pct === 100 ? PAL.green : cp === activeCp ? PAL.black : PAL.soft,
                        color: pct === 100 || cp === activeCp ? PAL.white : PAL.muted,
                        fontSize: 10,
                        fontWeight: 700,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {cp}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: PAL.black }}>{CP_SHORT_NAMES[cp]}</div>
                      <div style={{ fontSize: 10, color: PAL.muted }}>{answered}/{total} answered</div>
                    </div>
                    {pct === 100 && (
                      <span style={{ fontSize: 10, fontWeight: 700, color: PAL.green }}>Done</span>
                    )}
                  </button>
                );
              })}
            </div>
            <div style={{ marginTop: 12, textAlign: "right" }}>
              <button
                onClick={() => setJumpOpen(false)}
                style={{ fontSize: 12, fontWeight: 600, color: PAL.muted, background: "none", border: "none", cursor: "pointer" }}
              >
                Cancel (Esc)
              </button>
            </div>
          </div>
        </div>
      )}
    </OperatorShell>
  );
}
